import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, BadgeCheck, Plus, AlertTriangle, CheckCircle2,
  Eye, Trash2, ZoomIn, ImageOff, X, ExternalLink,
  ChevronDown, ChevronRight, FileText, RefreshCw, Layers,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState } from "@/components/ui";
import {
  expiryBand, bandStyle, daysToExpiry, fmtDate, isPdfPath,
  CATEGORY_DISPLAY, CATEGORY_ORDER, COLLAPSED_BY_DEFAULT,
} from "./licenceHelpers";
import { downloadLicencePack } from "./licenceExportPack";
import { Download, CheckSquare, Square } from "lucide-react";

// =============================================================================
// HrLicenceDetailScreen — /hr/licences/:employee_id
// Director/manager view of a single employee's licences. Shows every active
// licence grouped by category with Verify button (director-only) and delete
// action. Click photo to enlarge (lightbox). Entry point to add on-behalf.
// T-004 Commit D.
// =============================================================================

interface LicenceRow {
  licence_id:      string;
  licence_type_id: string;
  licence_number:  string | null;
  issue_date:      string | null;
  expiry_date:     string | null;
  state_issued:    string | null;
  verified_at:     string | null;
  verified_by_user_id: string | null;
  front_photo_url: string | null;
  back_photo_url:  string | null;
  notes:           string | null;
  external_url:    string | null;
  created_at:      string;
  licence_type: {
    code:                   string;
    display_name:           string;
    category:               string;
    issuing_body:           string | null;
    requires_verification:  boolean;
    external_system:        string | null;
  } | null;
}

interface EmployeeProfile {
  employee_id:        string;
  first_name:         string | null;
  last_name:          string | null;
  email:              string | null;
  mobile:             string | null;
  primary_role_name:  string | null;
  employment_type:    string | null;
  is_active:          boolean;
}

export default function HrLicenceDetailScreen() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const user = useRequireAuth();
  const queryClient = useQueryClient();

  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const [zoomLabel, setZoomLabel] = useState<string>("");
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  // Task #14 — bulk export pack selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exporting,   setExporting]   = useState<{ done: number; total: number } | null>(null);
  // Inductions and other noisy categories collapse by default — click to expand.
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());

  const isDirector     = hasRole(user, "director");
  const isManagerOrUp  = hasRole(user, "manager");
  // T-044: self-verification is blocked at the RPC layer when >=2 directors
  // exist. Mirror in the UI so the Verify button doesn't appear on your own
  // page — cleaner UX than showing it and having it error on click.
  const isOwnPage      = !!user?.employee_id && user.employee_id === employeeId;

  // Dismiss lightbox on Escape
  useEffect(() => {
    if (!zoomUrl) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setZoomUrl(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [zoomUrl]);

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------
  const { data: employee, isLoading: empLoading } = useQuery({
    queryKey: ["hr_licences_employee", employeeId],
    enabled: !!employeeId && isManagerOrUp,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employees")
        .select("employee_id, first_name, last_name, email, mobile, primary_role_name, employment_type, is_active")
        .eq("employee_id", employeeId!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as EmployeeProfile | null;
    },
  });

  const { data: licences, isLoading: licLoading } = useQuery({
    queryKey: ["hr_licences_detail", employeeId],
    enabled: !!employeeId && isManagerOrUp,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select(`
          licence_id, licence_type_id, licence_number, issue_date, expiry_date,
          state_issued, verified_at, verified_by_user_id,
          front_photo_url, back_photo_url, notes, external_url, created_at,
          licence_type:licence_types (
            code, display_name, category, issuing_body,
            requires_verification, external_system
          )
        `)
        .eq("employee_id", employeeId!)
        .eq("is_active", true)
        .order("expiry_date", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as unknown as LicenceRow[];
    },
  });

  // Batch-fetch signed URLs for every photo (front + back)
  useEffect(() => {
    if (!licences) return;
    const paths: string[] = [];
    for (const l of licences) {
      if (l.front_photo_url) paths.push(l.front_photo_url);
      if (l.back_photo_url)  paths.push(l.back_photo_url);
    }
    if (paths.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.storage
        .from("licence-documents")
        .createSignedUrls(paths, 3600);
      if (cancelled || !data) return;
      const map: Record<string, string> = {};
      for (const item of data) {
        if (item.path && item.signedUrl) {
          map[item.path] = item.signedUrl;
        }
      }
      setSignedUrls(map);
    })();
    return () => { cancelled = true; };
  }, [licences]);

  // -------------------------------------------------------------------------
  // Mutations — verify + delete
  // -------------------------------------------------------------------------
  const verifyMutation = useMutation({
    mutationFn: async (licenceId: string) => {
      const { error } = await supabase.schema("app").rpc("verify_licence", {
        p_licence_id: licenceId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hr_licences_detail", employeeId] });
      queryClient.invalidateQueries({ queryKey: ["hr_licences_all"] });
      queryClient.invalidateQueries({ queryKey: ["my_licences"] });
      queryClient.invalidateQueries({ queryKey: ["my_licences_summary"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_pending_licences"] });
      setActionError(null);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message
                 : (err as { message?: string })?.message ?? "Verification failed";
      setActionError(msg);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (licenceId: string) => {
      const { error } = await supabase.schema("app").rpc("delete_licence", {
        p_licence_id: licenceId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hr_licences_detail", employeeId] });
      queryClient.invalidateQueries({ queryKey: ["hr_licences_all"] });
      setActionError(null);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message
                 : (err as { message?: string })?.message ?? "Delete failed";
      setActionError(msg);
    },
  });

  // -------------------------------------------------------------------------
  // Guards
  // -------------------------------------------------------------------------
  if (!isManagerOrUp) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 text-text-muted text-sm">
            Licence oversight is restricted to managers and directors.
          </div>
        </Card>
      </div>
    );
  }

  if (empLoading || licLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Spinner size={20} />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card>
          <div className="p-6 flex items-start gap-3">
            <AlertTriangle size={18} className="text-warn flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">Employee not found</div>
              <div className="text-sm text-text-muted">
                The employee_id in the URL doesn't match any record you can see.
              </div>
              <Link to="/hr/licences" className="text-accent text-sm hover:underline mt-2 inline-block">
                ← Back to licence oversight
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const list = licences ?? [];
  const byCategory = new Map<string, LicenceRow[]>();
  for (const l of list) {
    const cat = l.licence_type?.category ?? "other";
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(l);
  }

  const displayName = `${employee.first_name ?? ""} ${employee.last_name ?? ""}`.trim() || "(no name)";
  const pendingCount = list.filter(l => l.licence_type?.requires_verification && !l.verified_at).length;
  const warningCount = list.filter(l => expiryBand(l.expiry_date) === "warning").length;
  const overdueCount = list.filter(l => expiryBand(l.expiry_date) === "overdue").length;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto pb-16">
      <Link
        to="/hr/licences"
        className="text-text-muted text-sm flex items-center gap-1 hover:text-text mb-2"
      >
        <ArrowLeft size={14} /> Back to licence oversight
      </Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{displayName}</h1>
          <div className="text-text-muted text-[13px] mt-1">
            {employee.primary_role_name?.replace(/_/g, " ") ?? "—"}
            {employee.email && <> · {employee.email}</>}
            {employee.mobile && <> · {employee.mobile}</>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* T-313 / T-252 — entry point into the VOC module for this employee */}
          <Link to={`/hr/vocs/employee/${employee.employee_id}`}>
            <Button variant="secondary" icon={BadgeCheck}>VOCs</Button>
          </Link>
          {/* 15 Sep 2026 — a new starter's whole folder in one go */}
          <Link to={`/hr/licences/${employee.employee_id}/bulk`}>
            <Button variant="secondary" icon={Layers} title="Drop every ticket and induction at once — the AI reads them, you check, save all">Bulk add</Button>
          </Link>
          <Link to={`/hr/licences/${employee.employee_id}/new`}>
            <Button variant="primary" icon={Plus}>Add on-behalf</Button>
          </Link>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        <MiniStat label="Total"    value={list.length} />
        <MiniStat label="Pending"  value={pendingCount} tone={pendingCount > 0 ? "warning" : "ok"} />
        <MiniStat label="Expiring" value={warningCount} tone={warningCount > 0 ? "warning" : "ok"} />
        <MiniStat label="Overdue"  value={overdueCount} tone={overdueCount > 0 ? "danger"  : "ok"} />
      </div>

      {/* Task #14 — bulk export toolbar. Tick the licences you want, click
          Download pack, get a ZIP with proper filenames for client onboarding. */}
      {list.length > 0 && (
        <Card className="mb-4">
          <div className="p-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (selectedIds.size === list.length) {
                    setSelectedIds(new Set());
                  } else {
                    setSelectedIds(new Set(list.map(l => l.licence_id)));
                  }
                }}
                className="text-[12px] inline-flex items-center gap-1 text-text-muted hover:text-text"
              >
                {selectedIds.size === list.length && list.length > 0
                  ? <><CheckSquare size={13} className="text-accent"/> Deselect all</>
                  : <><Square size={13}/> Select all</>}
              </button>
              <span className="text-[11px] text-text-dim">
                {selectedIds.size} of {list.length} selected
              </span>
              {selectedIds.size > 0 && (
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="text-[11px] text-text-muted hover:text-lost ml-2"
                >
                  Clear
                </button>
              )}
            </div>
            <Button
              variant="primary"
              size="sm"
              icon={Download}
              disabled={selectedIds.size === 0 || !!exporting}
              onClick={async () => {
                const picked = list.filter(l => selectedIds.has(l.licence_id));
                if (picked.length === 0) return;
                setActionError(null);
                setExporting({ done: 0, total: 0 });
                try {
                  await downloadLicencePack({
                    employeeFirstName: employee.first_name ?? null,
                    employeeLastName:  employee.last_name ?? null,
                    licences: picked.map(l => ({
                      licence_type_display: l.licence_type?.display_name ?? null,
                      expiry_date:          l.expiry_date,
                      front_photo_url:      l.front_photo_url,
                      back_photo_url:       l.back_photo_url,
                    })),
                    onProgress: (done, total) => setExporting({ done, total }),
                  });
                } catch (err: any) {
                  setActionError(err?.message ?? "Export failed");
                } finally {
                  setExporting(null);
                }
              }}
            >
              {exporting
                ? `Building pack… ${exporting.done}/${exporting.total}`
                : `Download pack (${selectedIds.size})`}
            </Button>
          </div>
          <div className="px-3 pb-3 text-[10px] text-text-dim italic">
            ZIP with proper filenames — drop straight into a client compliance portal.
          </div>
        </Card>
      )}

      {isOwnPage && pendingCount > 0 && (
        <Card className="mb-4 border-accent/30">
          <div className="p-4 flex items-center gap-3 text-sm text-text-muted">
            <AlertTriangle size={16} className="text-accent flex-shrink-0" />
            <span>
              You're viewing your own licence record. Separation-of-duties means
              another director will verify your pending licences.
            </span>
          </div>
        </Card>
      )}

      {actionError && (
        <Card className="mb-4 border-danger/30">
          <div className="p-4 flex items-center gap-3 text-sm text-danger">
            <AlertTriangle size={16} className="flex-shrink-0" />
            <span>{actionError}</span>
          </div>
        </Card>
      )}

      {list.length === 0 ? (
        <Card>
          <div className="p-6">
            <EmptyState
              title="No licences yet"
              description={`${displayName} hasn't added any licences. You can add one on-behalf from the button above.`}
            />
          </div>
        </Card>
      ) : (
        CATEGORY_ORDER.filter(cat => byCategory.has(cat)).map(cat => {
          const rows           = byCategory.get(cat)!;
          const collapsedDflt  = COLLAPSED_BY_DEFAULT.has(cat);
          const isExpanded     = collapsedDflt ? expandedCats.has(cat) : true;
          const selectedInCat  = rows.filter(l => selectedIds.has(l.licence_id)).length;

          const toggle = () => {
            if (!collapsedDflt) return;
            setExpandedCats(prev => {
              const next = new Set(prev);
              next.has(cat) ? next.delete(cat) : next.add(cat);
              return next;
            });
          };

          return (
            <div key={cat} className="mb-5">
              <h2
                className={`text-[11px] font-semibold text-text-muted uppercase tracking-wider2 mb-2 px-1 flex items-center gap-1.5 ${
                  collapsedDflt ? "cursor-pointer hover:text-text select-none" : ""
                }`}
                onClick={toggle}
                role={collapsedDflt ? "button" : undefined}
                aria-expanded={collapsedDflt ? isExpanded : undefined}
              >
                {collapsedDflt && (
                  isExpanded
                    ? <ChevronDown size={12} className="opacity-70"/>
                    : <ChevronRight size={12} className="opacity-70"/>
                )}
                {CATEGORY_DISPLAY[cat] ?? cat}
                <span className="font-normal normal-case tracking-normal text-text-muted/80">
                  ({rows.length})
                </span>
                {collapsedDflt && !isExpanded && selectedInCat > 0 && (
                  <span className="font-normal normal-case tracking-normal text-accent text-[10px]">
                    · {selectedInCat} selected
                  </span>
                )}
                {collapsedDflt && !isExpanded && (
                  <span className="font-normal normal-case tracking-normal text-text-dim text-[10px] ml-auto italic">
                    tap to expand
                  </span>
                )}
              </h2>
              {isExpanded && (
                <div className="space-y-2">
                  {rows.map(l => (
                    <HrLicenceRow
                      key={l.licence_id}
                      licence={l}
                      frontUrl={l.front_photo_url ? signedUrls[l.front_photo_url] : undefined}
                      backUrl={l.back_photo_url ? signedUrls[l.back_photo_url] : undefined}
                      canVerify={!!(isDirector && !isOwnPage && l.licence_type?.requires_verification && !l.verified_at)}
                      canDelete={isManagerOrUp}
                      onVerify={() => verifyMutation.mutate(l.licence_id)}
                      onDelete={() => {
                        if (confirm(`Delete this ${l.licence_type?.display_name ?? "licence"}? The audit log keeps a record.`)) {
                          deleteMutation.mutate(l.licence_id);
                        }
                      }}
                      isActing={verifyMutation.isPending || deleteMutation.isPending}
                      renewTo={
                        `/hr/licences/${employeeId}/new?renewType=${encodeURIComponent(l.licence_type_id)}` +
                        (l.licence_number ? `&renewNumber=${encodeURIComponent(l.licence_number)}` : "") +
                        (l.state_issued  ? `&renewState=${encodeURIComponent(l.state_issued)}`   : "")
                      }
                      onZoom={(url, label) => { setZoomUrl(url); setZoomLabel(label); }}
                      selected={selectedIds.has(l.licence_id)}
                      onToggleSelect={() => {
                        setSelectedIds(prev => {
                          const next = new Set(prev);
                          next.has(l.licence_id) ? next.delete(l.licence_id) : next.add(l.licence_id);
                          return next;
                        });
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Lightbox */}
      {zoomUrl && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex flex-col"
          onClick={() => setZoomUrl(null)}
          role="dialog"
          aria-label={zoomLabel}
        >
          <div className="flex items-center justify-between p-4 text-white">
            <div className="text-sm font-medium truncate">{zoomLabel}</div>
            <button
              onClick={() => setZoomUrl(null)}
              className="p-2 rounded hover:bg-white/10"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center p-4 min-h-0">
            {isPdfPath(zoomUrl) ? (
              <iframe
                src={zoomUrl}
                title={zoomLabel}
                className="w-full h-full rounded bg-white"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={zoomUrl}
                alt={zoomLabel}
                className="max-w-full max-h-full object-contain select-none"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
          <div className="text-center text-white/60 text-xs pb-4">
            Tap outside or press Esc to close
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface MiniStatProps {
  label: string;
  value: number;
  tone?: "ok" | "warning" | "danger";
}

function MiniStat({ label, value, tone }: MiniStatProps) {
  const toneClass = tone === "danger"  ? "text-danger"
                  : tone === "warning" ? "text-warn"
                  : "text-text";
  return (
    <Card>
      <div className="p-3">
        <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted">
          {label}
        </div>
        <div className={`text-xl font-semibold mt-0.5 ${toneClass}`}>{value}</div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface HrLicenceRowProps {
  licence:   LicenceRow;
  frontUrl:  string | undefined;
  backUrl:   string | undefined;
  canVerify: boolean;
  canDelete: boolean;
  onVerify:  () => void;
  onDelete:  () => void;
  isActing:  boolean;
  renewTo:   string;
  onZoom:    (url: string, label: string) => void;
  // Task #14 — bulk export selection
  selected:        boolean;
  onToggleSelect:  () => void;
}

function HrLicenceRow({
  licence, frontUrl, backUrl, canVerify, canDelete,
  onVerify, onDelete, isActing, renewTo, onZoom,
  selected, onToggleSelect,
}: HrLicenceRowProps) {
  const band  = expiryBand(licence.expiry_date);
  const style = bandStyle(band);
  const days  = daysToExpiry(licence.expiry_date);
  const needsVerify = licence.licence_type?.requires_verification && !licence.verified_at;
  const label = licence.licence_type?.display_name ?? "Licence";

  return (
    <Card className={`${style.cardBg} ${selected ? "ring-2 ring-accent" : ""}`}>
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Tickbox — Task #14 bulk export selection */}
          <button
            type="button"
            onClick={onToggleSelect}
            className={`mt-1 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
              selected
                ? "bg-accent border-accent text-ink"
                : "border-text-muted/50 hover:border-accent"
            }`}
            aria-label={selected ? "Deselect for export" : "Select for export"}
            title={selected ? "Deselect for export" : "Select for export"}
          >
            {selected && <CheckSquare size={13} strokeWidth={2.5}/>}
          </button>
          {/* Photo thumbs */}
          <div className="flex gap-2 flex-shrink-0">
            <PhotoThumb
              url={frontUrl} label={`${label} — front`} sideLabel="Front"
              isPdf={isPdfPath(licence.front_photo_url)} onZoom={onZoom}
            />
            {backUrl !== undefined && (
              <PhotoThumb
                url={backUrl} label={`${label} — back`} sideLabel="Back"
                isPdf={isPdfPath(licence.back_photo_url)} onZoom={onZoom}
              />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="font-semibold text-[14px] leading-tight">
                  {label}
                </div>
                <div className="text-[11px] text-text-muted font-mono mt-0.5">
                  {licence.licence_number ?? "(no number)"}
                  {licence.state_issued && <> · {licence.state_issued}</>}
                </div>
                {licence.licence_type?.issuing_body && (
                  <div className="text-[11px] text-text-muted mt-0.5">
                    Issued by {licence.licence_type.issuing_body}
                  </div>
                )}
              </div>
              {needsVerify
                ? <Pill label="Pending verification" tone="warning" small />
                : licence.verified_at
                  ? <Pill label="Verified" tone="approved" small />
                  : null}
            </div>

            <div className="mt-2 text-[12px]">
              {licence.expiry_date ? (
                <span className={`font-medium ${style.text}`}>
                  Expires {fmtDate(licence.expiry_date)}
                  {days !== null && band === "warning" && <> · {days}d left</>}
                  {days !== null && band === "overdue" && <> · {Math.abs(days)}d overdue</>}
                </span>
              ) : (
                <span className="text-text-muted">No expiry</span>
              )}
              {licence.issue_date && (
                <span className="text-text-muted"> · issued {fmtDate(licence.issue_date)}</span>
              )}
            </div>

            {licence.notes && (
              <p className="text-[12px] text-text-muted mt-2 line-clamp-2">{licence.notes}</p>
            )}

            {licence.external_url && (
              <a
                href={licence.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[12px] text-accent hover:underline mt-2"
              >
                <ExternalLink size={12} />
                View in {licence.licence_type?.external_system ?? "portal"}
              </a>
            )}

            {/* Actions */}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {canVerify && (
                <Button
                  variant="primary"
                  icon={BadgeCheck}
                  size="sm"
                  onClick={onVerify}
                  disabled={isActing}
                >
                  Verify
                </Button>
              )}
              {/* T-391 — one-click renew. Prefills the add screen with this
                  type + number; app.add_licence auto-archives the old row
                  (T-238) so history is kept and nothing needs deleting. */}
              <Link to={renewTo}>
                <Button
                  variant={band === "overdue" || band === "warning" ? "primary" : "secondary"}
                  icon={RefreshCw}
                  size="sm"
                >
                  Renew
                </Button>
              </Link>
              {canDelete && (
                <Button
                  variant="ghost"
                  icon={Trash2}
                  size="sm"
                  onClick={onDelete}
                  disabled={isActing}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface PhotoThumbProps {
  url:       string | undefined;
  label:     string;
  sideLabel: string;
  isPdf?:    boolean;
  onZoom:    (url: string, label: string) => void;
}

function PhotoThumb({ url, label, sideLabel, isPdf, onZoom }: PhotoThumbProps) {
  if (!url) {
    return (
      <div
        className="w-20 h-20 flex items-center justify-center bg-ink-raised rounded border border-line text-text-muted text-[10px] font-mono relative"
        aria-label={`${sideLabel} photo missing`}
      >
        <ImageOff size={16} />
      </div>
    );
  }
  // T-391 — PDFs can't render in an <img>; show a document tile that opens
  // the file in a new tab (browsers render PDFs natively).
  if (isPdf) {
    return (
      <button
        type="button"
        onClick={() => window.open(url, "_blank", "noopener")}
        className="w-20 h-20 rounded border border-line bg-ink-raised relative group flex flex-col items-center justify-center gap-1 hover:border-accent transition-colors"
        aria-label={`Open ${label} PDF`}
        title="Open PDF in a new tab"
      >
        <FileText size={22} className="text-accent" />
        <span className="text-[9px] font-mono uppercase text-text-muted group-hover:text-text">
          PDF cert
        </span>
        <div className="absolute bottom-0 left-0 right-0 text-center text-[9px] text-white bg-black/50 py-0.5 font-mono uppercase">
          {sideLabel}
        </div>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onZoom(url, label)}
      className="w-20 h-20 rounded border border-line overflow-hidden bg-ink-raised relative group cursor-zoom-in"
      aria-label={`Enlarge ${label}`}
    >
      <img src={url} alt={label} className="w-full h-full object-cover" />
      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
        <ZoomIn
          size={18}
          className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
        />
      </div>
      <div className="absolute bottom-0 left-0 right-0 text-center text-[9px] text-white bg-black/50 py-0.5 font-mono uppercase">
        {sideLabel}
      </div>
    </button>
  );
}
